import { useEffect, useState } from "react";
import { Button, Center, Loader, Stack, Text } from "@mantine/core";
import { openModal, closeAllModals } from "@mantine/modals";

const QR_SIZE = 220;

// What the Website items in the Certification drawer sections open instead of going straight to the
// link: a QR code to scan with a phone, or a button to carry on to the site here. The QR library is
// imported only when this actually opens (it's only ever needed on a click, and the drawer is on
// every page), and the code is drawn as a PNG data URL, which the Content Security Policy's img-src
// already allows. Dark modules on a white background with a margin are the library's defaults and
// what keeps it scannable in dark mode too, so they're deliberately not themed.
function WebsiteQrPrompt({ url }) {
  const [qrCode, setQrCode] = useState(null);
  const [failed, setFailed] = useState(false);

  let host = url;
  try {
    host = new URL(url).host;
  } catch {
    // Show the raw value if it somehow isn't parseable - the nav only offers http(s) URLs anyway.
  }

  useEffect(() => {
    let cancelled = false;
    import("qrcode")
      .then((module) => (module.default ?? module).toDataURL(url, { width: QR_SIZE, margin: 2, errorCorrectionLevel: "M" }))
      .then((dataUrl) => {
        if (!cancelled) setQrCode(dataUrl);
      })
      .catch((err) => {
        console.error("Couldn't create QR code", err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <Stack align="center" spacing="sm">
      <Text size="sm" align="center">
        Scan this QR code with your phone&apos;s camera to open the website on your phone, or continue to open it here.
      </Text>

      <Center sx={{ width: QR_SIZE, height: QR_SIZE }}>
        {qrCode ? (
          <img src={qrCode} width={QR_SIZE} height={QR_SIZE} alt={`QR code for ${host}`} style={{ borderRadius: 8 }} />
        ) : failed ? (
          <Text size="sm" c="dimmed" align="center">
            Couldn&apos;t create a QR code. You can still continue to the website.
          </Text>
        ) : (
          <Loader size="sm" />
        )}
      </Center>

      <Text size="xs" c="dimmed" align="center" sx={{ wordBreak: "break-all" }}>
        {host}
      </Text>

      {/* Stacked rather than side by side - "Continue to website" doesn't fit next to Cancel in a
          modal this narrow (it clipped), and would be worse still on a small phone. */}
      <Stack spacing="xs" sx={{ width: "100%" }} mt="xs">
        <Button
          fullWidth
          onClick={() => {
            window.open(url, "_blank", "noopener,noreferrer");
            closeAllModals();
          }}
        >
          Continue to website
        </Button>
        <Button fullWidth variant="default" onClick={() => closeAllModals()}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}

export function openWebsiteQrPrompt({ url, title }) {
  openModal({ title, centered: true, size: "sm", children: <WebsiteQrPrompt url={url} /> });
}
